/**
 * Placeholder substitution for panel text.
 *
 * `{taskName}` style tokens are replaced with values from the render context.
 * Substitution produces a STRING that the renderer puts in a text node — there
 * is no HTML path here, by design (see the security posture note in
 * `packages/db-types/src/panel-definition.ts`).
 *
 * An unknown placeholder renders LITERALLY rather than as an empty string. A
 * visible `{taksName}` in the UI is an authoring bug someone will notice and
 * fix; a silent blank is one that ships.
 *
 * "Unknown" means NOT AN OWN PROPERTY of the values map — the same guard, for the
 * same reason, as `resolveSlot` and the `conditionalText` condition in
 * `Panel.tsx`. The placeholder grammar is a bare identifier, which includes
 * `constructor`, `toString` and `valueOf`; a plain `values[name]` reaches
 * `Object.prototype`, finds a function and stringifies it, so `{constructor}`
 * would print `function Object() { [native code] }` into the panel. Inherited
 * properties are not values the caller passed.
 */

/** Matches `{name}` where name is a simple identifier. */
const PLACEHOLDER_PATTERN = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

export function substitutePlaceholders(
  text: string,
  values: Record<string, string | number | boolean> | undefined,
): string {
  if (!values) return text;
  return text.replace(PLACEHOLDER_PATTERN, (whole, name: string) => {
    if (!Object.prototype.hasOwnProperty.call(values, name)) return whole;
    const value = values[name];
    if (value === undefined || value === null) return whole;
    return String(value);
  });
}
